import { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { familyHandleProblem, normalizeFamilyHandle } from '@twinden/shared';
import { api, ApiError } from '../../lib/api';
import { appLocale } from '../../lib/format';
import { useT } from '../../i18n';

const CHECK_DELAY_MS = 400;

export type FamilyHandleCheck =
  | { kind: 'same' }
  | { kind: 'invalid' }
  | { kind: 'reserved'; handle: string }
  | { kind: 'checking' }
  | { kind: 'free'; handle: string }
  | { kind: 'taken'; handle: string };

interface FamilyNameHandleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  family: {
    id: string;
    name: string;
    handle: string;
    handleChangesLeft?: number;
  };
  /** Roots may change the handle (D216); admins only rename. */
  canChangeHandle: boolean;
  /** Renames through the family hook, so the switcher updates too. */
  rename: (name: string) => Promise<unknown>;
  /** After a save: reload the page's family and the family list. */
  onSaved: () => Promise<unknown> | void;
}

// Manage family → Name and handle (M59, D216): the name for everyone who
// manages the family, the handle for roots, with a live availability check.
export function FamilyNameHandleDialog({
  isOpen,
  onClose,
  family,
  canChangeHandle,
  rename,
  onSaved,
}: FamilyNameHandleDialogProps) {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState(family.name);
  const [rawHandle, setRawHandle] = useState(family.handle);
  const [check, setCheck] = useState<FamilyHandleCheck>({ kind: 'same' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(family.name);
    setRawHandle(family.handle);
    setCheck({ kind: 'same' });
  }, [isOpen, family.name, family.handle]);

  const handle = normalizeFamilyHandle(rawHandle);
  const changesLeft = family.handleChangesLeft;
  const noneLeft = changesLeft === 0;

  // The live check: format and reserved words here, taken on the server.
  useEffect(() => {
    if (!isOpen || !canChangeHandle) return;
    if (handle === family.handle) {
      setCheck({ kind: 'same' });
      return;
    }
    const problem = familyHandleProblem(handle);
    if (problem === 'invalid') {
      setCheck({ kind: 'invalid' });
      return;
    }
    if (problem === 'reserved') {
      setCheck({ kind: 'reserved', handle });
      return;
    }
    let cancelled = false;
    setCheck({ kind: 'checking' });
    const timer = setTimeout(() => {
      api
        .checkFamilyHandle(handle)
        .then(({ data }) => {
          if (cancelled) return;
          setCheck(
            data.available
              ? { kind: 'free', handle }
              : data.reason === 'reserved'
                ? { kind: 'reserved', handle }
                : data.reason === 'invalid'
                  ? { kind: 'invalid' }
                  : { kind: 'taken', handle }
          );
        })
        // Couldn't ask: let the save decide (the database is the judge).
        .catch(() => !cancelled && setCheck({ kind: 'free', handle }));
    }, CHECK_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, canChangeHandle, handle, family.handle]);

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== '' && trimmedName !== family.name;
  const handleChanged = canChangeHandle && check.kind === 'free';
  const handleBlocked =
    canChangeHandle && check.kind !== 'same' && check.kind !== 'free';
  const canSave =
    trimmedName !== '' &&
    !handleBlocked &&
    (nameChanged || handleChanged) &&
    !(handleChanged && noneLeft);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (nameChanged) await rename(trimmedName);
      if (handleChanged) await api.setFamilyHandle(family.id, handle);
      await onSaved();
      toast({ title: t('account.familyHandle.saved'), status: 'success' });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'HANDLE_TAKEN') {
        setCheck({ kind: 'taken', handle });
      } else if (
        err instanceof ApiError &&
        err.code === 'HANDLE_CHANGE_LIMIT'
      ) {
        toast({
          title: t('account.familyHandle.limit', {
            date: err.retryAt
              ? new Date(err.retryAt).toLocaleDateString(appLocale(), {
                  day: 'numeric',
                  month: 'long',
                })
              : '',
          }),
          status: 'warning',
        });
      } else {
        toast({
          title: t('account.familyHandle.saveFailed'),
          description:
            err instanceof ApiError
              ? err.message
              : t('account.profile.unknownError'),
          status: 'error',
        });
      }
      // A name saved before a refused handle still shows.
      if (nameChanged) await onSaved();
    } finally {
      setSaving(false);
    }
  };

  const status = (() => {
    switch (check.kind) {
      case 'checking':
        return (
          <HStack spacing={2} color="muted">
            <Spinner size="xs" />
            <Text fontSize="sm">{t('account.familyHandle.checking')}</Text>
          </HStack>
        );
      case 'free':
        return (
          <Text fontSize="sm" color="green.600" _dark={{ color: 'green.300' }}>
            {t('account.familyHandle.free', { handle: check.handle })}
          </Text>
        );
      case 'taken':
        return (
          <Text fontSize="sm" color="danger">
            {t('account.familyHandle.taken', { handle: check.handle })}
          </Text>
        );
      case 'reserved':
        return (
          <Text fontSize="sm" color="danger">
            {t('account.familyHandle.reserved', { handle: check.handle })}
          </Text>
        );
      case 'invalid':
        return (
          <Text fontSize="sm" color="danger">
            {t('account.familyHandle.invalid')}
          </Text>
        );
      default:
        return null;
    }
  })();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={4} borderRadius="28px">
        <ModalHeader>{t('account.familyHandle.title')}</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={5}>
            <FormControl>
              <FormLabel fontSize="sm">
                {t('account.admin.familyName')}
              </FormLabel>
              <Input
                h="48px"
                autoFocus
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void save();
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">
                {t('account.familyHandle.label')}
              </FormLabel>
              {canChangeHandle ? (
                <>
                  <InputGroup>
                    <InputLeftElement
                      h="48px"
                      color="muted"
                      pointerEvents="none"
                    >
                      @
                    </InputLeftElement>
                    <Input
                      h="48px"
                      value={rawHandle}
                      maxLength={40}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      isDisabled={noneLeft}
                      aria-invalid={handleBlocked && check.kind !== 'checking'}
                      // The field shows its own @; a pasted one is dropped.
                      onChange={(event) =>
                        setRawHandle(event.target.value.replace(/^\s*@+/, ''))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void save();
                      }}
                    />
                  </InputGroup>
                  <VStack align="stretch" spacing={1} mt={2}>
                    {status}
                    <FormHelperText mt={0} fontSize="sm" color="muted">
                      {noneLeft
                        ? t('account.familyHandle.noneLeft')
                        : changesLeft !== undefined
                          ? t('account.familyHandle.changesLeft', {
                              count: changesLeft,
                            })
                          : t('account.familyHandle.hint')}
                    </FormHelperText>
                    {check.kind === 'free' && (
                      <Text fontSize="sm" color="muted">
                        {t('account.familyHandle.links')}
                      </Text>
                    )}
                  </VStack>
                </>
              ) : (
                <>
                  <Text fontSize="17px" fontWeight={600}>
                    @{family.handle}
                  </Text>
                  <FormHelperText fontSize="sm" color="muted">
                    {t('account.familyHandle.rootsOnly')}
                  </FormHelperText>
                </>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" colorScheme="gray" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            isDisabled={!canSave}
            isLoading={saving}
            onClick={() => void save()}
          >
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
